import React from "react";
import { createPortal } from 'react-dom';
import {useState, useEffect, useCallback, useRef} from 'react';
import { Button, Spin, Alert } from 'antd';
import {Controller} from 'react-hook-form';
import {useQuery, gql, useMutation} from '@apollo/client';
import { ColumnsType } from "antd/es/table";
import InputNumber from "../../../components/forms/InputNumber";
import SettingsCard from "./SettingsCard";
import {useModal, useQueryVariables} from '../../../utils/hooks';
import { DriverTemplate, UserRole } from "../../../__generated__/graphql";
import {useCurrentUser} from '../../../utils/CurrentUserContext';
import FormModal from '../../../components/modals/FormModal';
import SingleLineField from '../../../components/forms/SingleLineField';
import DropdownField from '../../../components/forms/DropdownField';
import MultiLineField from '../../../components/forms/MultiLineField';
import HiddenInput from '../../../components/forms/HiddenInput';
import Table from "../../../components/lists/Table/Table";
import {PencilIcon} from '@heroicons/react/24/outline';
import ActionButton, {ActionHandler} from '../../../components/lists/Table/shared/ActionButton';
import FormItem from '../../../components/forms/utils/FormItem';
import { useFormContext } from "react-hook-form";

const getColumns = (actions?: {onEdit: ActionHandler<DriverTemplate>}): ColumnsType<DriverTemplate> => [
  {
    key: "name",
    title: "Name",
    dataIndex: "name",
  },
  {
    key: "code",
    title: "Template Code",
    dataIndex: "templateCode",
    render: (templateCode: string) => <textarea className="w-full" rows={3} value={templateCode} readOnly={true} />,
  },
  {
    key: "renderMode",
    title: "Render Mode",
    dataIndex: "renderMode",
  },
  ...actions? [
  {
    key: "actions",
    title: "",
    render: (row) => <div className="px-3">
      <ActionButton row={row} onClick={actions.onEdit} Icon={PencilIcon} />
    </div>,
  }
  ] as ColumnsType<DriverTemplate> : []


];
const template = '_id, name, renderMode, templateCode, publisherId, maxTeasers'

export const GET_ALL_TEMPLATES = gql`
  query DriverTemplateMany($page: Int, $perPage: Int, $filters: DriverTemplateFilterArgs) {
    templates: DriverTemplateMany(page: $page, perPage: $perPage, filters: $filters) {
      items{
        ${template}
      }
      meta {
        totalItems
      }
    }
  }
`
const CREATE_TEMPLATE = gql`
  mutation Mutation($record: CreateDriverTemplateInput!) {
    DriverTemplateCreateOne(record: $record) {
      ${template}
    }
  }
`

export const UPDATE_TEMPLATE = gql`
  mutation Mutation($id: ObjectId!, $record: UpdateDriverTemplateInput!) {
    DriverTemplateUpdateById(_id: $id, record: $record) {
      ${template}
    }
  }
`

const TemplateFormContent = () => {
  const { watch, setValue } = useFormContext();
  const renderMode = watch("renderMode");
  const isContimoWidget = renderMode === "CONTIMO_WIDGET";
  const [showCustomiser, setShowCustomiser] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const customiserUrl = process.env.REACT_APP_CUSTOMISER_URL || process.env.VITE_CUSTOMISER_URL || 'http://localhost:9000/';

  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    const handleMessage = (event: MessageEvent) => {
        if (!showCustomiser) return;

        if (event.data?.type === 'CONTIMO_CONFIG_READY') {
            clearTimeout(timer);
            setIsLoading(false);
            
            if (iframeRef.current?.contentWindow) {
                const currentConfigStr = watch('templateCode');
                let currentConfig = {};
                try {
                    currentConfig = JSON.parse(currentConfigStr);
                } catch (e) {
                    // Ignore error
                }
                
                iframeRef.current.contentWindow.postMessage({
                    type: 'CONTIMO_CONFIG_LOAD',
                    payload: {
                        config: currentConfig
                    }
                }, '*');
            }
        } else if (event.data?.type === 'CONTIMO_CONFIG_SAVE') {
            const newConfig = event.data.payload.config;
            setValue('templateCode', JSON.stringify(newConfig, null, 2), { shouldDirty: true, shouldValidate: true });
            setShowCustomiser(false);
        } else if (event.data?.type === 'CONTIMO_CONFIG_CANCEL') {
            setShowCustomiser(false);
        }
    };

    if (showCustomiser) {
        setIsLoading(true);
        setError(null);
        window.addEventListener('message', handleMessage);
        
        timer = setTimeout(() => {
            setError('UI failed to load');
            setIsLoading(false);
        }, 10000);
    }
    
    return () => {
        window.removeEventListener('message', handleMessage);
        clearTimeout(timer);
    };
  }, [showCustomiser, setValue, watch]);

  const handleOpenCustomiser = () => {
      setShowCustomiser(true);
  };

  return (
    <>
      <HiddenInput htmlfor="publisherId" />

      <SingleLineField
        top
        label="Name"
        htmlfor="name"
        required
      />
      <MultiLineField
        label="Template code"
        htmlfor="templateCode"
        required
        rows={10}
        rules={{
          validate: (value) => {
            if (isContimoWidget) {
              try {
                JSON.parse(value);
                return true;
              } catch (e) {
                return "Invalid JSON";
              }
            }
            return true;
          }
        }}
      />
      {isContimoWidget && (
        <div style={{ marginBottom: 24 }}>
          <Button onClick={handleOpenCustomiser}>
             Customise Widget
          </Button> 
        </div>
      )}
      {showCustomiser && createPortal(
          <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 10000,
              background: 'white',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
          }}>
              {isLoading && <Spin size="large" tip="Loading Customiser..." />}
              {error && (
                  <div className="flex flex-col gap-4 items-center">
                      <Alert message={error} type="error" showIcon />
                      <Button onClick={() => setShowCustomiser(false)}>
                          Close
                      </Button>
                  </div>
              )}
              <iframe
                ref={iframeRef}
                src={customiserUrl}
                allow="clipboard-write"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: (isLoading || error) ? 'none' : 'block'
                }}
                title="Contimo Customiser"
              />
          </div>,
          document.body
      )}
      <DropdownField
        label="Render mode"
        options={[
          { label: 'Friendly iframe', value: "FRIENDLY_IFRAME" },
          { label: 'Inline', value: "INLINE" },
          { label: 'Contimo Widget', value: "CONTIMO_WIDGET" }
        ]}
        htmlfor="renderMode"
        required
      />
      {!isContimoWidget && (
        <FormItem label="Max. traffic drivers" top>
          <Controller
            name='maxTeasers'
            render={({ field }) => (
              <InputNumber
                className='w-full max-w-xs'
                size='large'
                min={1}
                {...field}
              />
            )}
          />
        </FormItem>
      )}
    </>
  );
};

const Templates = () => {
  const {user, isAdmin} = useCurrentUser()
  const {variables, setPagination, setFilters} = useQueryVariables()

  const [template, setTemplate] = useState<any>();
  const [modalVisible, showModal, hideModal] = useModal()

  const { loading, error, data } = useQuery(GET_ALL_TEMPLATES, {
    variables
  });

  const [createTemplate] = useMutation(CREATE_TEMPLATE, {
    refetchQueries: ['DriverTemplateMany']
  })
  const [updateTemplate] = useMutation(UPDATE_TEMPLATE)

  const handleOk = async (record: any) => {
    if (template) {
      const { _id, __typename, publisherId, ...updatedRecord } = record
      await updateTemplate({ variables: { id: template._id, record: updatedRecord } })
    } else {
      await createTemplate({
        variables: { record }
      })
    }
  }

  const handleEditTemplate = (template: any) => {
    setTemplate(template)
    showModal()
  }

  const tableProps = isAdmin
  ? {
    actionButtonText: "Add Template",
    onActionButtonClick: showModal,
    columns: getColumns({onEdit: handleEditTemplate})
  }
  : {
    columns: getColumns()
  }
  return (
    <SettingsCard
      title="Templates"
      description="A list of traffic driving Templates."
    >
      <Table
        data={data?.templates.items || []}
        selectable={false}
        loading={loading}
        pagination={{
          total: data ? data?.templates.meta.totalItems : 0,
          pageSize: variables.perPage,
          current: variables.page
        }}
        onChange={(pagination, filters) => {
          setPagination(pagination)
          setFilters(filters)
        }}
        {...tableProps}
      />
      <FormModal
        open={modalVisible}
        title={`${template ? 'Edit' : 'Add'} Template`}
        okText={template ? 'Save' : 'Add'}
        defaultValues={template || { publisherId: user.companyId, renderMode: 'CONTIMO_WIDGET' }}
        onCancel={() => {
          hideModal()
          setTemplate(undefined)
        }}
        onOk={handleOk}
      >
        <TemplateFormContent />
      </FormModal>
    </SettingsCard>
  );
};

export default Templates;
